<template>
  <div
      class=" font-sans bg-base-100 text-neutral-200 antialiased transition-all duration-300 z-50 flex flex-col border border-neutral-800"
      :class="layoutClasses"
  >
    <!-- Top Header / Navigation Window controls -->
    <div class="flex items-center justify-between border-b border-neutral-800 px-4 py-3 select-none">
      <div class="flex items-center gap-2">
        <div
            class="flex h-5 w-5 items-center justify-center rounded-sm bg-neutral-800 text-xs font-bold text-neutral-400 border border-neutral-700">
          ✨
        </div>
        <span class="text-sm font-medium text-neutral-300">Katabia AI Task Assistant</span>
      </div>

      <!-- Layout Mode Toggles -->
      <div class="flex items-center gap-1.5">
        <button
            @click="layoutMode = 'float'"
            title="Float Window"
            class="rounded p-1 hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors"
            :class="{ 'bg-neutral-800 text-white': layoutMode === 'float' }"
        >
          <PhExcludeSquare/>
        </button>
        <button
            @click="layoutMode = 'dock'"
            title="Dock Sidebar"
            class="rounded p-1 hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors"
            :class="{ 'bg-neutral-800 text-white': layoutMode === 'dock' }"
        >
          <PhSquareSplitHorizontal/>
        </button>
        <button
            @click="layoutMode = 'fullscreen'"
            title="Full Screen"
            class="rounded p-1 hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors"
            :class="{ 'bg-neutral-800 text-white': layoutMode === 'fullscreen' }"
        >
          <PhFrameCorners/>
        </button>
        <button
            @click="layoutMode = 'hidden'"
            title="Float Window"
            class="rounded p-1 hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors"
            :class="{ 'bg-neutral-800 text-white': layoutMode === 'hidden' }"
        >
          <PhMinus/>
        </button>
      </div>
    </div>

    <!-- Scrollable Chat Stream Content -->
    <div class="flex-1 overflow-y-auto p-4 space-y-6" ref="chatContainer">
      <!-- Chat history sequence matching the uploaded layout reference -->
      <div v-for="item in conversation" :key="item.id" class="space-y-4">

        <!-- User Query Bubble Block -->
        <div v-if="item.type === 'user'" class="flex justify-end pl-12">
          <div
              class="rounded-2xl bg-[#252525] px-4 py-3 text-sm leading-relaxed text-neutral-200 border border-neutral-800 max-w-xl">
            {{ item.content }}
          </div>
        </div>

        <!-- AI Process & Execution Response Blocks -->
        <div v-else-if="item.type === 'ai'" class="space-y-4 pr-12">
          <!-- Thinking Tracer Timeline indicator -->
          <div v-if="item.duration" class="flex items-center gap-2 text-xs font-medium text-neutral-500">
            <span>Worked for {{ item.duration }}</span>
            <span class="inline-block w-1.5 h-1.5 rounded-full bg-neutral-700 animate-pulse"></span>
          </div>

          <!-- Structured Result Text Paragraph -->
          <p class="text-sm leading-relaxed text-neutral-300 font-normal">
            <span class="font-semibold text-white">Created</span> 📄
            <span
                class="underline decoration-neutral-600 underline-offset-2 font-medium text-neutral-200 cursor-pointer hover:text-white">
              {{ item.taskTitle }}
            </span>
            in 🗂️ <span class="font-medium text-neutral-200">{{ item.destination }}</span>. {{ item.summary }}
          </p>

          <!-- Component Action Box UI Sub-module Card -->
          <div class="rounded-xl border border-neutral-800 bg-[#202020] p-4 flex flex-col gap-3 max-w-md">
            <div>
              <div class="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1">Status Action</div>
              <div class="text-sm font-medium text-neutral-200">{{ item.actionLabel }}</div>
            </div>
            <div class="flex items-center gap-2">
              <button
                  @click="triggerFakeAction(item.id)"
                  class="inline-flex items-center gap-1.5 rounded-lg border border-neutral-700 bg-[#2a2a2a] px-3 py-1.5 text-xs font-medium text-neutral-200 hover:bg-neutral-700 hover:border-neutral-600 transition-colors cursor-pointer"
              >
                <span>Show changes</span>
                <span class="text-[10px]">↩</span>
              </button>
            </div>
          </div>

          <!-- Action bar shortcuts underneath response block -->
          <div class="flex items-center gap-3 text-neutral-500 text-xs border-b border-neutral-900 pb-2">
            <button class="hover:text-neutral-300 transition-colors cursor-pointer" title="Copy">📋</button>
            <button class="hover:text-neutral-300 transition-colors cursor-pointer" title="Add to page">➕</button>
            <button class="hover:text-neutral-300 transition-colors cursor-pointer" title="Good response">👍</button>
            <button class="hover:text-neutral-300 transition-colors cursor-pointer" title="Bad response">👎</button>
            <button
                class="hover:text-neutral-300 text-amber-500/80 transition-colors cursor-pointer flex items-center gap-1"
                title="Undo">
              <span>↩</span> Undo
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Message Composition & Search Control Foot -->
    <div class="p-4 border-t border-neutral-800 bg-[#191919]">
      <form @submit.prevent="handleSendMessage"
            class="relative rounded-xl border border-neutral-800 bg-[#202020] focus-within:border-neutral-700 transition-colors">
        <textarea
            v-model="inputValue"
            rows="2"
            placeholder="Ask Katabia AI..."
            class="w-full resize-none bg-transparent px-4 py-3 text-sm text-neutral-200 placeholder-neutral-500 focus:outline-none"
            @keydown.enter.prevent="handleSendMessage"
        ></textarea>

        <div class="flex items-center justify-between border-t border-neutral-900/60 px-3 py-2">
          <div class="flex items-center gap-1.5">
            <button
                type="button"
                @click="loadFakePrompt"
                class="inline-flex items-center gap-1 rounded-lg bg-[#252525] border border-neutral-800 px-2.5 py-1 text-xs text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700/50 transition-colors cursor-pointer"
            >
              <span>+</span> Review for me
            </button>
          </div>

          <div class="flex items-center gap-2">
            <span class="text-[11px] font-mono px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-500">Auto</span>
            <button
                type="submit"
                :disabled="!inputValue.trim()"
                class="flex h-6 w-6 items-center justify-center rounded-md bg-neutral-700 text-neutral-400 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                :class="{ 'bg-blue-600 text-white hover:bg-blue-500': inputValue.trim() }"
            >
              ↑
            </button>
          </div>
        </div>
      </form>
    </div>

    <button v-show="layoutMode === 'hidden' " @click="layoutMode = 'dock'"
            class="fixed z-50 w-14 h-14 bg-white text-black m-6 flex justify-center items-center rounded-full right-0 bottom-0 p-4">
      <ph-robot class="w-12 h-12"></ph-robot>
    </button>
  </div>
</template>

<script setup>
import {ref, computed, nextTick} from 'vue';
import subscription from "../../channels/agent_channel"
import {
  PhExcludeSquare,
  PhFrameCorners,
  PhMinus, PhRobot,
  PhSparkle,
  PhSquareSplitHorizontal,
  PhWindmill
} from "@phosphor-icons/vue";

// Docking, Floating, or Fullscreen display modes
const layoutMode = ref('hidden');
const inputValue = ref('');
const chatContainer = ref(null);

// Initial reactive timeline mock array mirroring the image mockup structure
const conversation = ref([
  {
    id: 1,
    type: 'user',
    content: "Can you help me identify what's wrong with this machine"
  },
  {
    id: 2,
    type: 'ai',
    duration: '20s',
    taskTitle: 'Implement comprehensive audit',
    destination: 'Second machine',
    summary: 'to inspect issues.',
    actionLabel: 'Created inspection task'
  }
]);

// Dynamically compute target window geometry states depending on navigation selection
const layoutClasses = computed(() => {
  switch (layoutMode.value) {
    case 'dock':
      return ' top-0 w-92 right-0 h-screen  shadow-2xl';
    case 'fullscreen':
      return 'fixed inset-0 h-screen w-screen';
    case 'hidden':
      return 'w-0 h-0 inset-0';
    case 'float':
    default:
      return 'fixed overflow-hidden bottom-6 border border-white/20 right-6 h-[600px] w-[440px] rounded-2xl shadow-xl';
  }
});

// Auto scrolls message window container to ground bottom
const scrollToBottom = async () => {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

// Prefills prompt input field to easily text actions
const loadFakePrompt = () => {
  inputValue.value = 'Review my application authentication logic and setup test cases.';
};

// Simulates processing user inquiries on submission
const handleSendMessage = () => {
  if (!inputValue.value.trim()) return;

  const currentPrompt = inputValue.value;

  subscription.send({
    message: {
      content: currentPrompt,
      action: "send_message"
    }
  })
  conversation.value.push({
    id: Date.now(),
    type: 'user',
    content: currentPrompt
  });

  inputValue.value = '';
  scrollToBottom();

  // Fake system response processing latency block
  setTimeout(() => {
    conversation.value.push({
      id: Date.now() + 1,
      type: 'ai',
      duration: '4s',
      taskTitle: `Audit matching request`,
      destination: 'Active machine Backlog',
      summary: 'Generated tracking metrics schema documentation alongside automated component verification hooks.',
      actionLabel: 'Created system component log definitions'
    });
    scrollToBottom();
  }, 1200);
};

// Native interactive target feedback for internal action boxes
const triggerFakeAction = (id) => {
  alert(`Triggered action context: File diff rendering layer for log element instance [ID: ${id}]`);
};
</script>
